import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import cedarsLogo from 'images/employer-logos/cedars_sinai_square_logo.webp';
import northwellLogo from 'images/employer-logos/northwell_health_square_logo.webp';
import yaleLogo from 'images/employer-logos/yale_new_haven_health_square_logo.webp';
import gradientBlobsPath from 'images/illustrations/gradient_blobs.svg';
import gradientBlobsWidePath from 'images/illustrations/gradient_blobs_wide.svg';
import Cookie from 'js-cookie';
import throttle from 'just-throttle';
import { useForm } from 'react-hook-form';

import { StatefulInput } from 'shared/components/ihcl/input';
import { Center } from 'shared/components/ihcl/positioning';
import { styled, Theme, withStyle } from 'shared/components/ihcl/styled';
import { Button } from 'shared/components/ihcl/v2/button';
import { toast } from 'shared/components/ihcl/v2/toast';
import sharedConstants from 'shared/constants/constants.json';
import { pathToVariant } from 'shared/helpers/activateABTest';
import getFetchSignupSettings from 'shared/helpers/getFetchSignupSettings';
import { genExternalLinkHandler } from 'shared/helpers/linking';
import Sentry, { maskName } from 'shared/helpers/sentry';
import { trackHiringSignUp } from 'shared/helpers/tracking';

import {
  Onboarding,
  OnboardingContext,
  OnboardingTitle as OnboardingTitleBase,
} from '../../containers/Onboarding';
import estimatedJobMatchCount from '../../helpers/estimatedJobMatchCount';
import {
  convertStateToAttributes,
  getAugmentedOnboardingData,
} from '../../helpers/talentProfileConversions';
import validateTalentProfile, {
  validateTalentProfileField,
} from '../../helpers/validateTalentProfile';
import JobCard from './JobCard';
import OnboardingTestimonial from './OnboardingTestimonial';

const title = 'Where can we send your job matches?';

const FooterText = styled('div', ({ $theme }) => ({
  color: $theme.colors.grayLabel,
  fontSize: $theme.sizing.scale550,
  marginTop: '16px',
  marginRight: '16px',
  marginLeft: '16px',
  textAlign: 'center',
}));

const OnboardingTitle = withStyle(
  OnboardingTitleBase,
  ({
    $additionalTopSpacing = false,
  }: {
    $additionalTopSpacing: boolean;
    $theme: Theme;
  }) => ({
    marginTop: $additionalTopSpacing ? '40px' : '32px',
    marginBottom: '20px',
    marginRight: '16px',
    marginLeft: '16px',
  })
);

const CardsContainer = styled('div', {
  position: 'relative',
  width: '100%',
  height: '70vw',
  overflow: 'hidden',
  maxHeight: '37vh',
  marginTop: '32px',
});

const CardsContainerBackground = styled<
  'div',
  { $showWideBackground?: boolean }
>('div', ({ $showWideBackground = false }) => ({
  position: 'absolute',
  width: '100%',
  height: '70vw',
  maxHeight: '37vh',
  backgroundImage: `url(${
    $showWideBackground ? gradientBlobsWidePath : gradientBlobsPath
  })`,
  backgroundSize: $showWideBackground ? 'auto 100%' : 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: $showWideBackground ? 'top center' : 'center',
  animationDuration: '2s',
  animationName: {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 100,
    },
  },
}));

const SidePaddingWrapper = styled('div', {
  paddingRight: '16px',
  paddingLeft: '16px',
});

const OnboardingEmail = ({
  nextAction,
  stepNumber,
  urlQuery = {},
}: OnboardingEmailProps) => {
  const {
    history,
    onboardingBasePath,
    onboardingData,
    onboardingOptions,
    onboardingUpdates,
    profileType = 'nurse',
    setOnboardingData,
  } = useContext(OnboardingContext);

  const formRef = useRef(null);
  const initialValues = {
    email: onboardingData.email || '',
  };
  const {
    clearErrors,
    handleSubmit,
    register,
    setError,
    formState: { errors },
  }: any = useForm({
    mode: 'onTouched',
    defaultValues: initialValues,
  });
  const formErrors = {
    ...errors,
    ...errors.user,
  };
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const variantGroup = onboardingBasePath.includes('tech_onboard')
    ? 'techOnboarding'
    : 'onboarding';
  const variant = pathToVariant(onboardingBasePath, variantGroup);

  const jobMatchCount = estimatedJobMatchCount(
    onboardingData,
    onboardingOptions
  );

  let matchCountExperiment = null;
  if (jobMatchCount) {
    if (variant === 'jarvis') {
      matchCountExperiment = 'JobMatchOnboardingExperimentTreatment';
    } else {
      matchCountExperiment = 'JobMatchOnboardingExperimentControl';
    }
  }

  useEffect(() => {
    window.mixpanel.track('Onboard Email Page Viewed', {
      Experiment: matchCountExperiment,
      'Match Count': jobMatchCount,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  type ProfileAttributes = {
    onboard_variant: string;
    signup_intent: string;
    timezone_from_onboarding: string;
    talent_tags?: string[];
    source?: string;
    referrer_code?: string;
    google_click_id?: string;
    [key: string]: any;
  };

  const signUp = async (formData) => {
    const profile = {
      ...formData,
    };
    delete profile.fullName;
    const validationErrors = validateTalentProfile(
      profile,
      Object.keys(profile)
    );
    if (validationErrors.size === 0) {
      const type = profileType === 'tech' ? 'tech' : 'talent';

      const signUpUrl = `/users/sign_up/${type}.json`;
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let profileAttributes: ProfileAttributes = {
        onboard_variant: variant,
        signup_intent: 'hiring',
        timezone_from_onboarding: browserTimezone,
      };

      if (matchCountExperiment) {
        profileAttributes.talent_tags = [matchCountExperiment];
      }
      if (urlQuery.source) {
        profileAttributes.source = urlQuery.source;
      }
      sharedConstants.PROFILE_TRACKING_PARAM_NAMES.forEach((utmParamName) => {
        if (urlQuery[utmParamName]) {
          profileAttributes[utmParamName] = urlQuery[utmParamName];
        }
      });
      if (urlQuery.ref) {
        profileAttributes.referrer_code = urlQuery.ref;
      }
      const gclid = Cookie.get('gclid');
      if (gclid) {
        profileAttributes.google_click_id = gclid;
      }
      let localOnboardingUpdates = {};
      if (typeof window.localforage !== 'undefined') {
        localOnboardingUpdates =
          await window.localforage.getItem('onboardingUpdates');
      } else {
        localOnboardingUpdates = convertStateToAttributes(
          onboardingUpdates,
          Object.keys(onboardingUpdates),
          profile
        );
      }

      if (type === 'tech') {
        profileAttributes.type = 'TechProfile';
      }

      profileAttributes = {
        ...profileAttributes,
        ...localOnboardingUpdates,
        ...onboardingUpdates,
        current_onboard_step: stepNumber + 1,
      };
      if (profileAttributes.user_attributes) {
        delete profileAttributes.user_attributes;
      }
      const body: { gen_password?: boolean; user: { [key: string]: any } } = {
        user: {
          ...profile,
          talent_profile_attributes: profileAttributes,
          remember_me: true,
        },
      };

      if (!profile || !profile.password) {
        body.gen_password = true;
      }
      fetch(signUpUrl, getFetchSignupSettings(body))
        .then((data) => data.json())
        .then(async (json) => {
          setIsFormSubmitting(false);
          if (json && json.errors) {
            Object.keys(json.errors).forEach((key) => {
              setError(key, {
                type: 'validationError',
                message: json.errors[key].join('; '),
              });
            });
          } else {
            if (typeof window.localforage !== 'undefined') {
              try {
                await window.localforage.removeItem('onboardingUpdates');
              } catch (err) {
                console.error('Error removing onboardingUpdates:', err);
              }
            }
            setOnboardingData(
              getAugmentedOnboardingData(
                onboardingData,
                json,
                onboardingOptions
              )
            );
            trackHiringSignUp(json);
            if (json.user_id && json.user_id.toString()) {
              window.mixpanel.alias(json.user_id.toString());
            } else {
              console.error(`cannot alias user_id: ${json.user_id}`);
            }
            window.mixpanel.register({ 'User Type': 'talent' });
            window.mixpanel.track(`Onboard Step ${stepNumber} Completed`);
            window.mixpanel.identify(json.user_id.toString());
            Sentry.setUser({
              user_type: 'talent',
              talent_profile_id: json.id,
              user_id: json.user_id,
              username: maskName(json.full_name),
            });
            history.push(nextAction);
          }
        })
        .catch((error) => {
          console.error(error);
          setIsFormSubmitting(false);
          const errorMsg =
            'Oops, there was a problem connecting ' +
            'to the server. Please check your internet connection ' +
            'and try again, or refresh the page.';
          toast.error(errorMsg);
        });
    }
  };
  const blurField = (evt, fieldName, fieldProps) => {
    fieldProps.onBlur(evt);
  };
  const changeField = (evt, fieldName, fieldProps) => {
    if (formErrors[fieldName]) {
      clearErrors(`user.${fieldName}`);
    }
    fieldProps.onChange(evt);
  };
  const emailProps = register('email', {
    validate: (value) => validateTalentProfileField('email', value),
  });
  const submitHandler = handleSubmit(signUp);
  const throttledSubmitHandler = useMemo(
    () => throttle((evt) => submitHandler(evt), 1000, { leading: true }),
    []
  );

  let scaleValues = [0.4, 0.5, 0.6];
  let offsetValues: string[] = ['35%', '15%', '-5%'];
  let topValues: string[] = ['-30%', '-20%', '-5%'];
  if (document.body.offsetWidth > 460) {
    scaleValues = [0.5, 0.65, 0.8];
    offsetValues = ['45%', '22.5%', '0'];
    topValues = ['-20%', '-10%', '10%'];
  }
  if (document.body.offsetWidth > 600) {
    scaleValues = [0.5, 0.65, 0.8];
    offsetValues = ['55%', '33%', '3%'];
    topValues = ['-5%', '0', '5%'];
  }

  return (
    <form
      action="#"
      onSubmit={(evt) => {
        evt.preventDefault();
        setIsFormSubmitting(true);
        throttledSubmitHandler(evt);
      }}
      ref={formRef}
    >
      <Onboarding
        currentStep={stepNumber}
        flushWrapperSides
        isNextDisabled={isFormSubmitting || Boolean(formErrors.email)}
        nextAction={nextAction}
        nextButtonSubmits
        validateBeforeSubmit={() => false}
        stepName="Email"
      >
        <Center>
          <OnboardingTitle>{title}</OnboardingTitle>
          <SidePaddingWrapper>
            <StatefulInput
              caption={formErrors.email && formErrors.email.message}
              collapseCaptionPadding
              initialState={{ value: initialValues.email }}
              label="Email address"
              type="email"
              inputMode="email"
              {...emailProps}
              onBlur={(evt) => blurField(evt, 'email', emailProps)}
              onChange={(evt) => changeField(evt, 'email', emailProps)}
              error={!!formErrors.email}
            />
          </SidePaddingWrapper>
          {formErrors.email &&
            formErrors.email.message === 'has already been taken' && (
              <Button
                variant="minimal"
                onClick={() => {
                  window.location.href = '/users/sign_in';
                }}
              >
                Sign In?
              </Button>
            )}

          {/* TODO: WEB-5880 different cards / layout for techs? */}
          <CardsContainer>
            <CardsContainerBackground $showWideBackground>
              <JobCard
                animationDuration="1s"
                hospitalLogo={yaleLogo}
                hospitalName="Yale New Haven Health"
                scale={scaleValues[0]}
                rotation="-8deg"
                rightOffset={offsetValues[0]}
                top={topValues[0]}
                jobTitle="Operating Room / Surgical"
                bonus="$8000"
                hasRelocationAssistance
                location="New Haven, CT"
                recruiterName="David"
                message="Hi! There's an..."
              />
              <JobCard
                animationDuration="1.5s"
                hospitalLogo={northwellLogo}
                hospitalName="Northwell Health"
                scale={scaleValues[1]}
                rotation="4deg"
                rightOffset={offsetValues[1]}
                top={topValues[1]}
                jobTitle="Emergency Department"
                hasRelocationAssistance
                location="New York, NY"
                recruiterName="Sara"
                message="Hi! I love your..."
              />
              <JobCard
                animationDuration="2s"
                hospitalLogo={cedarsLogo}
                hospitalName="Cedars Sinai"
                scale={scaleValues[2]}
                rotation="14deg"
                rightOffset={offsetValues[2]}
                top={topValues[2]}
                jobTitle="Medical / Surgical Coordinator"
                bonus="$10,000"
                hasRelocationAssistance
                location="Los Angeles, CA"
                recruiterName="Charu"
                message="Hi! We have full... "
              />
            </CardsContainerBackground>
          </CardsContainer>
          <OnboardingTestimonial
            attributionLine="#humbleBrag from:"
            author="Lisa Marie, Emergency RN"
            quote="Very good at matching your profile and potential employers. I got my results quickly, and got the job I wanted."
          />
          <FooterText>
            By signing up you agree to Incredible Health&apos;s{' '}
            <a
              href="https://www.incrediblehealth.com/terms.html"
              onClick={genExternalLinkHandler(true)}
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://www.incrediblehealth.com/privacy.html"
              onClick={genExternalLinkHandler(true)}
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
          </FooterText>
        </Center>
      </Onboarding>
    </form>
  );
};

type OnboardingEmailProps = {
  nextAction: string;
  stepNumber: number;
  urlQuery?: { source?: string; ref?: any };
};

export default OnboardingEmail;
